import { useEffect, useRef, useState } from "react";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import { useConfirm } from "../../components/ConfirmDialog/ConfirmDialog";
import jsPDF from "jspdf";
import React from "react";
import { createPortal } from "react-dom";
import {
    getVehicleAssignments,
    getVehicleAssignmentById,
    deleteVehicleAssignment,
    getAssignmentChain,
    getSupplierTypeOptions,
    getVehicleModelOptions,
    getLocationOptions,
    getBranchesByLocation,
    getAssignmentTypeOptions,
} from "../../services/vehicleAssignment_service";
import { getFinancialYears } from "../../services/supplier_service";
import { useNavigate } from "react-router-dom";
import { FiX, FiFileText, FiGitBranch, FiFilter, FiChevronDown, FiMoreVertical } from "react-icons/fi";
import "./VehicleAssignments.css";
import { getCurrentUser } from "../../services/auth_service";
import {
    isSuperadmin,
    hasPermission,
} from "../../pages/utils/permissions";

function getInitials(name) {
    if (!name) return "?";

    return name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0].toUpperCase())
        .join("");
}

const AVATAR_COLORS = [
    { bg: "#fee2e2", color: "#b91c1c" },
    { bg: "#dbeafe", color: "#1d4ed8" },
    { bg: "#dcfce7", color: "#15803d" },
    { bg: "#fef9c3", color: "#a16207" },
    { bg: "#ede9fe", color: "#6d28d9" },
    { bg: "#ffedd5", color: "#c2410c" },
];

function avatarStyle(name) {
    if (!name) return AVATAR_COLORS[0];

    const idx = name.charCodeAt(0) % AVATAR_COLORS.length;
    return AVATAR_COLORS[idx];
}

const NEXT_STAGES_FROM_ID = {
    1: [2, 3],
    2: [],
    3: [],
};

const CLOSED_TYPE_IDS = [2, 3];
const DEFAULT_STAGE_ID = "1";
const DOC_FIELDS = [
    { key: "allotment_document_photo", label: "Allotment Document" },
    { key: "vehicle_photo_front", label: "Vehicle Front" },
    { key: "vehicle_photo_back", label: "Vehicle Back" },
    { key: "vehicle_photo_left", label: "Vehicle Left" },
    { key: "vehicle_photo_right", label: "Vehicle Right" },
];
const API_BASE_URL = (process.env.REACT_APP_API_URL || "http://localhost:8000/api").replace(/\/+$/, "");
const API_ORIGIN = API_BASE_URL.replace(/\/api$/i, "");

function resolveMediaUrl(value) {
    if (!value) return null;
    const raw = String(value).trim();
    if (!raw || /^data:/i.test(raw) || /^blob:/i.test(raw)) return raw || null;
    if (/^https?:\/\//i.test(raw)) return raw;
    if (raw.startsWith("/")) return `${API_ORIGIN}${raw}`;
    return `${API_ORIGIN}/${raw}`;
}

const isImageUrl = (url) => {
    if (!url) return false;
    const clean = String(url).split("?")[0].split("#")[0];
    return /\.(jpg|jpeg|png|webp|gif|bmp|svg)$/i.test(clean);
};

const docCount = (item) =>
    DOC_FIELDS.filter(({ key }) => !!item[key]).length;

const getAvailableStageIds = (item) => {
    if (!item.is_active) return [];
    return NEXT_STAGES_FROM_ID[item.assignment_type_id] || [];
};

function getVehiclePhoto(item, vehicles = []) {
    const vehicle = vehicles.find((v) => String(v.id) === String(item?.vehicle_id));
    const value =
        item?.vehicle_photo_url ||
        item?.vehicle_photo ||
        item?.vehicle?.vehicle_photo_url ||
        item?.vehicle?.vehicle_photo ||
        item?.vehicle?.photo_url ||
        item?.vehicle?.photoUrl ||
        item?.vehicle?.image_url ||
        item?.vehicle?.imageUrl ||
        item?.vehicle_photo_front ||
        item?.vehicle_image_url ||
        item?.vehicle_image ||
        item?.vehicle_photo_path ||
        vehicle?.vehicle_photo_url ||
        vehicle?.vehicle_photo ||
        vehicle?.photo_url ||
        vehicle?.photoUrl ||
        vehicle?.image_url ||
        vehicle?.imageUrl ||
        null;
    return resolveMediaUrl(value);
}

function getSupplierPhoto(item, suppliers = []) {
    const supplier = suppliers.find((s) => String(s.id) === String(item?.supplier_id));
    const value =
        item?.supplier_photo_url ||
        item?.supplier?.supplier_photo_url ||
        item?.supplier?.photo_url ||
        item?.supplier?.photoUrl ||
        item?.supplier?.image_url ||
        item?.supplier?.imageUrl ||
        supplier?.supplier_photo_url ||
        supplier?.photo_url ||
        supplier?.photoUrl ||
        supplier?.image_url ||
        supplier?.imageUrl ||
        null;
    return resolveMediaUrl(value);
}

function getAssignedPhoto(item) {
    if (item?.driver_id) {
        const value =
            item?.driver_photo_url ||
            item?.driver?.driver_photo_url ||
            item?.driver?.photo_url ||
            item?.photo_url ||
            null;
        return { url: resolveMediaUrl(value), name: item.full_name || "Driver" };
    }
    if (item?.supplier_id) {
        const value =
            item?.supplier_photo_url ||
            item?.supplier?.supplier_photo_url ||
            item?.supplier?.photo_url ||
            null;
        return { url: resolveMediaUrl(value), name: item.supplier_name || "Supplier" };
    }
    return { url: null, name: null };
}

function getPartyType(item) {
    if (item.supplier_type) return item.supplier_type;
    if (item.driver_id) return "Own Fleet";
    return null;
}

const STAGE_BADGE_COLOR = {
    Dispatch: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
    Handover: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
    Recovery: { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
};
const OUTSTANDING_RANGES = [
    { label: "0 - 50K", min: 0, max: 50000 },
    { label: "50K - 1L", min: 50000, max: 100000 },
    { label: "1L - 2L", min: 100000, max: 200000 },
    { label: "2L - 3L", min: 200000, max: 300000 },
    { label: "3L - 4L", min: 300000, max: 400000 },
    { label: "4L - 5L", min: 400000, max: 500000 },
    { label: "5L - 6L", min: 500000, max: 600000 },
    { label: "6L - 7L", min: 600000, max: 700000 },
    { label: "7L - 8L", min: 700000, max: 800000 },
    { label: "8L - 9L", min: 800000, max: 900000 },
    { label: "9L - 10L", min: 900000, max: 1000000 },
    { label: "10L+", min: 1000000, max: null },
];

function toDateOnly(d) {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return null;
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
}
function isWithinDateRange(createdAt, fromDate, toDate) {
    if (!fromDate && !toDate) return true;
    if (!createdAt) return false;

    const created = toDateOnly(createdAt);
    if (!created) return false;

    if (fromDate) {
        const from = toDateOnly(fromDate);
        if (from && created < from) return false;
    }
    if (toDate) {
        const to = toDateOnly(toDate);
        if (to && created > to) return false;
    }
    return true;
}

const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "—";
    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
    }).format(amount);
};
const formatCurrencyPdf = (amount) => {
    if (amount === null || amount === undefined) return "-";
    const formatted = new Intl.NumberFormat("en-IN", {
        maximumFractionDigits: 0,
    }).format(amount);
    return `Rs. ${formatted}`;
};

export default function VehicleAssignments() {
    const navigate = useNavigate();
    const currentUser = getCurrentUser() || {};
    const confirm = useConfirm();

    const isSuperadminUser = isSuperadmin();
    const canViewAssignments =
        isSuperadminUser || hasPermission("assignments", "view");
    const canEditAssignments =
        isSuperadminUser || hasPermission("assignments", "edit");
    const canDeleteAssignments =
        isSuperadminUser || hasPermission("assignments", "delete");

    const hasAssignmentAccess =
        isSuperadminUser ||
        canViewAssignments ||
        canEditAssignments ||
        canDeleteAssignments;
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [financialYears, setFinancialYears] = useState([]);
    const [selectedFinancialYear, setSelectedFinancialYear] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [selectedType, setSelectedType] = useState("");
    const [typeOptions, setTypeOptions] = useState([]);
    const [selectedStageId, setSelectedStageId] = useState(DEFAULT_STAGE_ID);
    const [assignmentTypes, setAssignmentTypes] = useState([]);
    const [selectedModel, setSelectedModel] = useState("");
    const [modelOptions, setModelOptions] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState("");
    const [locationOptions, setLocationOptions] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState("");
    const [branchOptions, setBranchOptions] = useState([]);
    const [branchesLoading, setBranchesLoading] = useState(false);
    const [selectedOutstandingRange, setSelectedOutstandingRange] = useState("");
    const [selectedStatus, setSelectedStatus] = useState(""); // "", "active", "inactive"
    const ROWS_PER_PAGE = 15;
    const [currentPage, setCurrentPage] = useState(1);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [filtersPos, setFiltersPos] = useState({ top: 0, left: 0 });
    const filtersBtnRef = useRef(null);
    const toggleFilters = () => {
        if (!filtersOpen && filtersBtnRef.current) {
            const rect = filtersBtnRef.current.getBoundingClientRect();
            setFiltersPos({ top: rect.bottom + 8, left: rect.left - 150 });
        }
        setFiltersOpen((open) => !open);
    };

    const [actionMenuState, setActionMenuState] = useState({ id: null, top: 0, left: 0 });
    const toggleActionMenu = (e, id) => {
        e.stopPropagation();
        if (actionMenuState.id === id) {
            setActionMenuState({ id: null, top: 0, left: 0 });
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        setActionMenuState({ id, top: rect.bottom + 6, left: rect.right - 130 });
    };
    const closeActionMenu = () => setActionMenuState({ id: null, top: 0, left: 0 });

    const [stageMenuState, setStageMenuState] = useState({ id: null, top: 0, left: 0 });
    const toggleStageMenu = (e, id) => {
        e.stopPropagation();
        if (stageMenuState.id === id) {
            setStageMenuState({ id: null, top: 0, left: 0 });
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        setStageMenuState({ id, top: rect.bottom + 6, left: rect.left });
    };
    const closeStageMenu = () => setStageMenuState({ id: null, top: 0, left: 0 });

    const [viewModalItem, setViewModalItem] = useState(null);
    const [viewModalLoading, setViewModalLoading] = useState(false);
    const [viewModalError, setViewModalError] = useState(null);
    const openViewModal = async (item) => {
        setViewModalItem(item);
        setViewModalError(null);
        setViewModalLoading(true);
        try {
            const fresh = await getVehicleAssignmentById(item.id);
            setViewModalItem(fresh);
        } catch (err) {
            console.error("Failed to fetch assignment details", err);
            setViewModalError("Couldn't refresh full details — showing last known data.");
        } finally {
            setViewModalLoading(false);
        }
    };
    const closeViewModal = () => {
        setViewModalItem(null);
        setViewModalError(null);
    };
    const handlePrintView = async () => {
        if (!viewModalItem) return;

        const escapeHtml = (value) => String(value ?? "-")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");

        const textValue = (value) => escapeHtml(
            value === null || value === undefined || value === "" ? "-" : value
        );

        const formatDate = (value, withTime = false) => {
            if (!value) return "-";
            try {
                return new Date(value).toLocaleString("en-GB", withTime ? {
                    day: "2-digit", month: "2-digit", year: "numeric",
                    hour: "2-digit", minute: "2-digit"
                } : {
                    day: "2-digit", month: "2-digit", year: "numeric"
                });
            } catch {
                return value;
            }
        };

        const item = viewModalItem;
        const status = item.is_active ? "Active" : "Inactive";
        const personName = item.full_name || item.supplier_name || "Vehicle Assignment";
        const personPhotoUrl = getAssignedPhoto(item)?.url || getVehiclePhoto(item) || "";

        const field = (label, value) => `
            <div class="field">
                <div class="label">${escapeHtml(label)}</div>
                <div class="value">${textValue(value)}</div>
            </div>`;

        const section = (title, fields) => `
            <section class="section">
                <h2>${escapeHtml(title)}</h2>
                <div class="grid">${fields.join("")}</div>
            </section>`;

        const photoHtml = personPhotoUrl ? `
            <img class="person-photo" src="${escapeHtml(personPhotoUrl)}" alt="${escapeHtml(personName)}" />` : `
            <div class="person-photo initials">${escapeHtml(getInitials(personName))}</div>`;

        const docCards = DOC_FIELDS.map(({ key, label }) => {
            const url = resolveMediaUrl(item[key]);
            if (!url) {
                return `<div class="doc-card"><div class="doc-title">${escapeHtml(label)}</div><div class="doc-empty">Not uploaded</div></div>`;
            }
            return `<div class="doc-card"><div class="doc-title">${escapeHtml(label)}</div><div class="doc-empty">Uploaded document</div></div>`;
        }).join("");

        const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>${escapeHtml(item.unique_number || "Vehicle Assignment")}</title>
<style>
@page{size:A4 portrait;margin:10mm}
*{box-sizing:border-box}
html,body{margin:0;padding:0;background:#fff;color:#172033;font-family:Arial,Helvetica,sans-serif;-webkit-print-color-adjust:exact;print-color-adjust:exact}
body{font-size:10px}
.page{width:100%;max-width:190mm;margin:0 auto}
.header{height:43mm;background:linear-gradient(135deg,#0b225f 0%,#1d55bf 60%,#16a8b2 100%);color:#fff;border-radius:0 0 12px 12px;padding:9mm;display:flex;align-items:center;gap:7mm;position:relative;overflow:hidden}
.header:after{content:"";position:absolute;right:0;top:0;width:38mm;height:2mm;background:#20c4c7}
.person-photo{width:25mm;height:25mm;border-radius:50%;object-fit:cover;background:#fff;border:2mm solid rgba(255,255,255,.92);display:block;flex:0 0 auto}
.person-photo.initials{display:flex;align-items:center;justify-content:center;color:#12336e;font-size:18px;font-weight:800;background:#fff}
.header-info{min-width:0;flex:1}
.name{font-size:22px;font-weight:800;line-height:1.1;margin:0 0 3mm;letter-spacing:.1px}
.meta{font-size:10.5px;line-height:1.5;color:#e4efff;margin:0}
.submeta{font-size:8.5px;color:#bed3ff;margin-top:1.5mm}
.status{background:#dcfce7;color:#15803d;border-radius:20px;padding:2.8mm 5mm;font-size:9px;font-weight:800;white-space:nowrap}
.content{padding-top:7mm}
.section{margin:0 0 5mm;break-inside:avoid;page-break-inside:avoid}
.section h2{font-size:12px;line-height:1.2;margin:0 0 2.5mm;padding-left:3mm;border-left:1.5mm solid #2563eb;color:#10244c;text-transform:uppercase;letter-spacing:.2px}
.grid{display:grid;grid-template-columns:1fr 1fr;gap:2.2mm}
.field{min-height:13mm;border:1px solid #dbe5f1;border-radius:2.2mm;background:#f7faff;padding:2.4mm 3mm;display:flex;flex-direction:column;justify-content:center;break-inside:avoid;page-break-inside:avoid}
.label{font-size:6.8px;font-weight:800;color:#718096;text-transform:uppercase;letter-spacing:.35px;margin-bottom:1.1mm;line-height:1.1}
.value{font-size:9.6px;font-weight:700;color:#162a4a;line-height:1.25;overflow-wrap:anywhere}
.docs{display:grid;grid-template-columns:repeat(3,1fr);gap:2.5mm}
.doc-card{height:22mm;border:1px solid #dbe5f1;border-radius:2.2mm;background:#f8fbff;padding:3mm;break-inside:avoid}
.doc-title{font-size:7px;font-weight:800;color:#536783;text-transform:uppercase;margin-bottom:3mm}
.doc-empty{height:11mm;border:1px dashed #cbd5e1;border-radius:1.5mm;display:flex;align-items:center;justify-content:center;color:#94a3b8;font-size:7.5px;background:#fff}
.footer{border-top:1px solid #dbe5f1;margin-top:5mm;padding-top:2.5mm;color:#7a8799;font-size:7px;display:flex;justify-content:space-between}
@media print{.section,.field,.doc-card{break-inside:avoid;page-break-inside:avoid}}
</style>
</head>
<body>
<div class="page">
<header class="header">
${photoHtml}
<div class="header-info">
<div class="name">${escapeHtml(personName)}</div>
<div class="meta">${textValue(item.unique_number)} &nbsp;•&nbsp; ${textValue(item.vehicle_no)} &nbsp;•&nbsp; ${textValue(item.assignment_type)}</div>
<div class="submeta">Transaction: ${textValue(item.transaction_id)} &nbsp;•&nbsp; Chain: ${textValue(item.chain_id)}</div>
</div>
<div class="status">${escapeHtml(status.toUpperCase())}</div>
</header>
<main class="content">
${section("Overview", [
    field("Transaction Number", item.unique_number),
    field("Transaction ID", item.transaction_id),
    field("Assignment Type", item.assignment_type),
    field("Financial Year", item.financial_year_label),
    field("Parent Transaction", item.parent_unique_number),
    field("Chain ID", item.chain_id),
    field("Status", status),
    field("Remarks", item.remarks)
])}
${section("Driver", [
    field("Driver Name", item.full_name),
    field("Driver Code", item.driver_code),
    field("Mobile", item.mobile),
    field("Email", item.email)
])}
${section("Supplier", [
    field("Supplier Name", item.supplier_name),
    field("Supplier Code", item.supplier_code),
    field("Supplier Mobile", item.supplier_mobile),
    field("Supplier Type", item.supplier_type || getPartyType(item)),
    field("Outstanding Amount", formatCurrency(item.outstanding_amount))
])}
${section("Vehicle & Location", [
    field("Vehicle Number", item.vehicle_no),
    field("Display Number", item.display_no),
    field("Vehicle Model", item.vehicle_model),
    field("Odometer (KM)", item.vehicle_odometer_km),
    field("Service Location", item.location_name),
    field("Branch Name", item.branch_name)
])}
${section("Dates & Audit", [
    field("Assigned Date", formatDate(item.assigned_date)),
    field("Closure Date", formatDate(item.relieved_date)),
    field("Transaction Date", formatDate(item.transaction_date, true)),
    field("Created On", formatDate(item.created_at, true)),
    field("Created By", item.created_by_name),
    field("Updated By", item.updated_by_name)
])}
<section class="section">
<h2>Photos & Documents</h2>
<div class="docs">${docCards}</div>
</section>
<div class="footer"><span>Srinivasa Fleet Management Pro</span><span>${escapeHtml(item.unique_number || "Vehicle Assignment")}</span></div>
</main>
</div>
<script>
(function(){
 const imgs=[...document.images];
 Promise.all(imgs.map(img=>new Promise(resolve=>{if(img.complete)return resolve();img.onload=resolve;img.onerror=resolve;}))).then(()=>setTimeout(()=>window.print(),250));
})();
</script>
</body></html>`;

        const printWindow = window.open("", "_blank", "width=1100,height=850");
        if (!printWindow) {
            alert("Please allow pop-ups for this site to print the assignment.");
            return;
        }
        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
    };
    const [pdfGenerating, setPdfGenerating] = useState(false);
    const loadImageAsDataUrl = async (url) => {
        if (!url) return null;
        try {
            const res = await fetch(url, { cache: "no-store" });
            if (!res.ok) return null;
            const blob = await res.blob();
            return await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result || null);
                reader.onerror = () => resolve(null);
                reader.readAsDataURL(blob);
            });
        } catch (err) {
            console.warn("Image fetch error:", url, err);
            return null;
        }
    };

    const getImageDimensions = (dataUrl) =>
        new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
            img.onerror = () => resolve(null);
            img.src = dataUrl;
        });

    const imageFormatFromDataUrl = (dataUrl) => {
        if (dataUrl.startsWith("data:image/png")) return "PNG";
        if (dataUrl.startsWith("data:image/webp")) return "WEBP";
        return "JPEG";
    };
    const cropImageToCircle = async (dataUrl, size = 180) => {
        if (!dataUrl) return null;
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                try {
                    const canvas = document.createElement("canvas");
                    canvas.width = size;
                    canvas.height = size;
                    const ctx = canvas.getContext("2d");
                    ctx.clearRect(0, 0, size, size);
                    ctx.save();
                    ctx.beginPath();
                    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
                    ctx.closePath();
                    ctx.clip();
                    const scale = Math.max(size / img.naturalWidth, size / img.naturalHeight);
                    const w = img.naturalWidth * scale;
                    const h = img.naturalHeight * scale;
                    ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
                    ctx.restore();
                    resolve(canvas.toDataURL("image/png"));
                } catch {
                    resolve(dataUrl);
                }
            };
            img.onerror = () => resolve(dataUrl);
            img.src = dataUrl;
        });
    };

    const buildAssignmentPdf = async (item) => {
        const docImages = {};
        await Promise.all(DOC_FIELDS.map(async ({ key }) => {
            const url = resolveMediaUrl(item[key]);
            if (!url || !isImageUrl(url)) return;
            const dataUrl = await loadImageAsDataUrl(url);
            if (!dataUrl) return;
            const dims = await getImageDimensions(dataUrl);
            if (dims) docImages[key] = { dataUrl, dims };
        }));

        const assignedPhotoUrl = getAssignedPhoto(item).url;
        let personPhoto = null;
        if (assignedPhotoUrl) {
            const dataUrl = await loadImageAsDataUrl(assignedPhotoUrl);
            if (dataUrl) {
                const dims = await getImageDimensions(dataUrl);
                if (dims) {
                    personPhoto = {
                        dataUrl,
                        dims,
                        circleDataUrl: await cropImageToCircle(dataUrl),
                    };
                }
            }
        }

        const doc = new jsPDF({ unit: "pt", format: "a4" });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 38;
        const contentWidth = pageWidth - margin * 2;
        const footerY = pageHeight - 28;
        const colors = {
            navy: [10, 31, 92],
            blue: [37, 99, 235],
            cyan: [22, 166, 184],
            text: [15, 23, 42],
            muted: [100, 116, 139],
            border: [216, 226, 239],
            card: [247, 250, 255],
            white: [255, 255, 255],
            green: [21, 128, 61],
            greenBg: [220, 252, 231],
        };
        let y = 0;
        let pageNum = 1;

        const valueText = (value) => {
            if (value === null || value === undefined || value === "") return "-";
            return String(value);
        };

        const drawFooter = () => {
            doc.setDrawColor(...colors.border);
            doc.setLineWidth(0.5);
            doc.line(margin, footerY - 9, pageWidth - margin, footerY - 9);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(7.5);
            doc.setTextColor(...colors.muted);
            doc.text("Srinivasa Fleet Management Pro", margin, footerY + 4);
            doc.text(`Page ${pageNum}`, pageWidth - margin, footerY + 4, { align: "right" });
        };

        const addPage = () => {
            drawFooter();
            doc.addPage();
            pageNum += 1;
            y = 34;
        };

        const ensureSpace = (height) => {
            if (y + height > footerY - 16) addPage();
        };

        const sectionTitle = (title) => {
            ensureSpace(28);
            doc.setFillColor(...colors.blue);
            doc.roundedRect(margin, y + 1, 3.5, 14, 1.5, 1.5, "F");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(11);
            doc.setTextColor(...colors.text);
            doc.text(title.toUpperCase(), margin + 10, y + 12);
            y += 24;
        };

        const addFields = (fields) => {
            const gap = 6;
            const cols = 2;
            const cardW = (contentWidth - gap) / cols;
            const cardPad = 8;
            const labelSize = 6.7;
            const valueSize = 9.2;
            const lineH = 10;
            const rows = [];

            for (let i = 0; i < fields.length; i += cols) {
                const row = [fields[i] || null, fields[i + 1] || null];
                let maxLines = 1;
                row.forEach((itemField) => {
                    if (!itemField) return;
                    doc.setFont("helvetica", "bold");
                    doc.setFontSize(valueSize);
                    const wrapped = doc.splitTextToSize(valueText(itemField[1]), cardW - cardPad * 2);
                    maxLines = Math.max(maxLines, wrapped.length);
                });
                rows.push({ row, height: Math.max(27, 25 + (maxLines - 1) * lineH) });
            }

            rows.forEach(({ row, height }) => {
                ensureSpace(height + 7);
                row.forEach((itemField, colIndex) => {
                    if (!itemField) return;
                    const x = margin + colIndex * (cardW + gap);
                    const top = y;

                    doc.setFillColor(...colors.card);
                    doc.setDrawColor(...colors.border);
                    doc.setLineWidth(0.45);
                    doc.roundedRect(x, top, cardW, height, 5, 5, "FD");

                    doc.setFont("helvetica", "bold");
                    doc.setFontSize(labelSize);
                    doc.setTextColor(...colors.muted);
                    doc.text(String(itemField[0]).toUpperCase(), x + cardPad, top + 9);

                    doc.setFont("helvetica", "bold");
                    doc.setFontSize(valueSize);
                    doc.setTextColor(...colors.text);
                    const wrapped = doc.splitTextToSize(valueText(itemField[1]), cardW - cardPad * 2);
                    doc.text(wrapped, x + cardPad, top + 15.5, { baseline: "top" });
                });
                y += height + gap;
            });
            y += 2;
        };

        const addDocuments = () => {
            const gap = 10;
            const cols = 2;
            const cellW = (contentWidth - gap) / cols;
            const cellH = 148;
            const labelH = 20;

            for (let i = 0; i < DOC_FIELDS.length; i += cols) {
                ensureSpace(cellH + labelH + gap + 8);
                const rowItems = DOC_FIELDS.slice(i, i + cols);
                rowItems.forEach(({ key, label }, idx) => {
                    const x = margin + idx * (cellW + gap);
                    const top = y;
                    doc.setFont("helvetica", "bold");
                    doc.setFontSize(8);
                    doc.setTextColor(...colors.muted);
                    doc.text(label.toUpperCase(), x, top + 10);

                    const boxY = top + labelH;
                    doc.setFillColor(250, 252, 255);
                    doc.setDrawColor(...colors.border);
                    doc.roundedRect(x, boxY, cellW, cellH, 8, 8, "FD");

                    const img = docImages[key];
                    if (img) {
                        try {
                            const pad = 8;
                            const maxW = cellW - pad * 2;
                            const maxH = cellH - pad * 2;
                            const scale = Math.min(maxW / img.dims.width, maxH / img.dims.height, 1);
                            const w = img.dims.width * scale;
                            const h = img.dims.height * scale;
                            const ix = x + (cellW - w) / 2;
                            const iy = boxY + (cellH - h) / 2;
                            doc.addImage(img.dataUrl, imageFormatFromDataUrl(img.dataUrl), ix, iy, w, h);
                        } catch {
                            doc.setFont("helvetica", "italic");
                            doc.setFontSize(8);
                            doc.setTextColor(...colors.muted);
                            doc.text("Preview unavailable", x + cellW / 2, boxY + cellH / 2, { align: "center" });
                        }
                    } else if (resolveMediaUrl(item[key])) {
                        doc.setFont("helvetica", "normal");
                        doc.setFontSize(8);
                        doc.setTextColor(...colors.muted);
                        doc.text("Uploaded document", x + cellW / 2, boxY + cellH / 2 - 4, { align: "center" });
                        doc.setFontSize(7);
                        doc.text("Open in application to view", x + cellW / 2, boxY + cellH / 2 + 9, { align: "center" });
                    } else {
                        doc.setFont("helvetica", "italic");
                        doc.setFontSize(8);
                        doc.setTextColor(148, 163, 184);
                        doc.text("Not uploaded", x + cellW / 2, boxY + cellH / 2, { align: "center" });
                    }
                });
                y += labelH + cellH + gap;
            }
        };

        const headerH = 104;
        doc.setFillColor(...colors.navy);
        doc.rect(0, 0, pageWidth, headerH, "F");
        doc.setFillColor(...colors.blue);
        doc.rect(0, 0, pageWidth, 5, "F");
        doc.setFillColor(...colors.cyan);
        doc.rect(pageWidth - 95, 0, 95, 5, "F");

        const avatarSize = 58;
        const avatarX = margin + 8;
        const avatarY = 23;
        doc.setFillColor(...colors.white);
        doc.circle(avatarX + avatarSize / 2, avatarY + avatarSize / 2, avatarSize / 2 + 3, "F");

        if (personPhoto?.circleDataUrl) {
            try {
                doc.addImage(personPhoto.circleDataUrl, "PNG", avatarX, avatarY, avatarSize, avatarSize);
            } catch {
                doc.setFillColor(226, 232, 240);
                doc.circle(avatarX + avatarSize / 2, avatarY + avatarSize / 2, avatarSize / 2, "F");
            }
        } else {
            doc.setFillColor(226, 232, 240);
            doc.circle(avatarX + avatarSize / 2, avatarY + avatarSize / 2, avatarSize / 2, "F");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(18);
            doc.setTextColor(...colors.navy);
            doc.text(getInitials(item.full_name || item.supplier_name), avatarX + avatarSize / 2, avatarY + avatarSize / 2 + 6, { align: "center" });
        }

        const textX = avatarX + avatarSize + 17;
        const name = item.full_name || item.supplier_name || "Vehicle Assignment";
        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.setTextColor(...colors.white);
        doc.text(name, textX, 40);

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9.5);
        doc.setTextColor(219, 234, 254);
        doc.text(`${valueText(item.unique_number)}   |   ${valueText(item.vehicle_no)}   |   ${valueText(item.assignment_type)}`, textX, 59);
        doc.setFontSize(8.5);
        doc.setTextColor(191, 219, 254);
        doc.text(`Transaction: ${valueText(item.transaction_id)}   |   Chain: ${valueText(item.chain_id)}`, textX, 76);

        const status = item.is_active ? "ACTIVE" : "INACTIVE";
        const statusW = doc.getTextWidth(status) + 18;
        const statusX = pageWidth - margin - statusW;
        doc.setFillColor(item.is_active ? 220 : 254, item.is_active ? 252 : 226, item.is_active ? 231 : 226);
        doc.roundedRect(statusX, 38, statusW, 22, 11, 11, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8);
        doc.setTextColor(...(item.is_active ? colors.green : [185, 28, 28]));
        doc.text(status, statusX + statusW / 2, 52, { align: "center" });

        y = headerH + 22;
        sectionTitle("Overview");
        addFields([
            ["Transaction Number", item.unique_number],
            ["Transaction ID", item.transaction_id],
            ["Assignment Type", item.assignment_type],
            ["Financial Year", item.financial_year_label],
            ["Parent Transaction", item.parent_unique_number],
            ["Chain ID", item.chain_id],
            ["Status", item.is_active ? "Active" : "Inactive"],
            ["Remarks", item.remarks],
        ]);

        sectionTitle("Driver");
        addFields([
            ["Driver Name", item.full_name],
            ["Driver Code", item.driver_code],
            ["Mobile", item.mobile],
            ["Email", item.email],
        ]);

        sectionTitle("Supplier");
        addFields([
            ["Supplier Name", item.supplier_name],
            ["Supplier Code", item.supplier_code],
            ["Supplier Mobile", item.supplier_mobile],
            ["Supplier Type", item.supplier_type || getPartyType(item)],
            ["Outstanding Amount", formatCurrencyPdf(item.outstanding_amount)],
        ]);

        sectionTitle("Vehicle & Location");
        addFields([
            ["Vehicle Number", item.vehicle_no],
            ["Display Number", item.display_no],
            ["Vehicle Model", item.vehicle_model],
            ["Odometer (KM)", item.vehicle_odometer_km],
            ["Service Location", item.location_name],
            ["Branch Name", item.branch_name],
        ]);

        sectionTitle("Dates & Audit");
        addFields([
            ["Assigned Date", item.assigned_date ? new Date(item.assigned_date).toLocaleDateString("en-GB") : "-"],
            ["Closure Date", item.relieved_date ? new Date(item.relieved_date).toLocaleDateString("en-GB") : "-"],
            ["Transaction Date", item.transaction_date ? new Date(item.transaction_date).toLocaleString("en-GB") : "-"],
            ["Created On", item.created_at ? new Date(item.created_at).toLocaleString("en-GB") : "-"],
            ["Created By", item.created_by_name],
            ["Updated By", item.updated_by_name],
        ]);

        sectionTitle("Photos & Documents");
        addDocuments();

        drawFooter();
        return doc;
    };

    const handleDownloadView = async () => {
        if (!viewModalItem) return;
        setPdfGenerating(true);
        try {
            const doc = await buildAssignmentPdf(viewModalItem);
            const safeName = (viewModalItem.unique_number || viewModalItem.vehicle_no || "assignment")
                .toString()
                .trim()
                .replace(/\s+/g, "_");
            doc.save(`${safeName}.pdf`);
        } finally {
            setPdfGenerating(false);
        }
    };

    const [docModalItem, setDocModalItem] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewLabel, setPreviewLabel] = useState("");
    const [chainModalOpen, setChainModalOpen] = useState(false);
    const [chainModalRows, setChainModalRows] = useState([]);
    const [chainModalLoading, setChainModalLoading] = useState(false);
    const [chainModalError, setChainModalError] = useState(null);
    const [chainModalLabel, setChainModalLabel] = useState("");

    const loadAssignments = async (
        fyValue,
        typeValue,
        stageIdValue,
        modelValue,
        locationValue,
        branchValue,
        outstandingRangeValue
    ) => {
        try {
            setLoading(true);
            const opts =
                fyValue === "all" ? { allYears: true }
                    : fyValue ? { financialYearId: fyValue }
                        : {};
            if (typeValue) opts.supplierType = typeValue;
            if (stageIdValue) opts.assignmentTypeId = stageIdValue;   // FIXED
            if (modelValue) opts.vehicleModel = modelValue;
            if (locationValue) opts.locationId = locationValue;
            if (branchValue) opts.branchId = branchValue;
            if (outstandingRangeValue !== "" && outstandingRangeValue !== undefined) {
                const range = OUTSTANDING_RANGES[Number(outstandingRangeValue)];
                if (range) {
                    opts.outstandingMin = range.min;
                    if (range.max !== null) opts.outstandingMax = range.max;
                }
            }
            const data = await getVehicleAssignments(opts);
            setAssignments(data);
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    };

    const loadFinancialYears = async () => {
        try {
            const data = await getFinancialYears();
            setFinancialYears(data || []);
        } catch (err) {
            console.log(err);
        }
    };

    const loadSupplierTypeOptions = async () => {
        try {
            const data = await getSupplierTypeOptions();
            setTypeOptions((data || []).map((t) => t.supplier_type_name));
        } catch (err) {
            console.log(err);
        }
    };

    const loadVehicleModelOptions = async () => {
        try {
            const data = await getVehicleModelOptions();
            setModelOptions((data || []).map((m) => m.vehicle_model ?? m));
        } catch (err) {
            console.log(err);
        }
    };

    const loadLocationOptions = async () => {
        try {
            const data = await getLocationOptions();
            setLocationOptions(data || []);
        } catch (err) {
            console.log(err);
        }
    };
    const loadAssignmentTypeOptions = async () => {
        try {
            const data = await getAssignmentTypeOptions();
            setAssignmentTypes(data || []);
        } catch (err) {
            console.log(err);
        }
    };

    const loadBranchesForLocation = async (locationId) => {
        if (!locationId) {
            setBranchOptions([]);
            return;
        }
        try {
            setBranchesLoading(true);
            const data = await getBranchesByLocation(locationId);
            setBranchOptions(data || []);
        } catch (err) {
            console.log(err);
            setBranchOptions([]);
        } finally {
            setBranchesLoading(false);
        }
    };

    useEffect(() => {
        loadFinancialYears();
        loadSupplierTypeOptions();
        loadVehicleModelOptions();
        loadLocationOptions();
        loadAssignmentTypeOptions();
    }, []);

    useEffect(() => {
        setSelectedBranch("");
        loadBranchesForLocation(selectedLocation);
    }, [selectedLocation]);

    useEffect(() => {
        loadAssignments(
            selectedFinancialYear,
            selectedType,
            selectedStageId,
            selectedModel,
            selectedLocation,
            selectedBranch,
            selectedOutstandingRange
        );
    }, [
        selectedFinancialYear,
        selectedType,
        selectedStageId,
        selectedModel,
        selectedLocation,
        selectedBranch,
        selectedOutstandingRange,
    ]);

    useEffect(() => {
        setCurrentPage(1);
    }, [
        assignments,
        search,
        fromDate,
        toDate,
        selectedFinancialYear,
        selectedType,
        selectedStageId,
        selectedModel,
        selectedLocation,
        selectedBranch,
        selectedOutstandingRange,
        selectedStatus,
    ]);

    const handleDelete = async (id) => {
        if (!canDeleteAssignments) return;

        const ok = await confirm({
            title: "Delete Assignment",
            message: "Are you sure you want to delete this vehicle assignment? This action cannot be undone.",
            confirmLabel: "Delete",
            cancelLabel: "Cancel",
            variant: "danger",
        });

        if (!ok) return;

        try {
            await deleteVehicleAssignment(id);
            alert("Assignment deleted successfully");
            await loadAssignments(
                selectedFinancialYear,
                selectedType,
                selectedStageId,
                selectedModel,
                selectedLocation,
                selectedBranch,
                selectedOutstandingRange
            );
        } catch (err) {
            console.log(err);
            alert("Delete failed");
        }
    };


    const openStageForm = (item, targetTypeId) => {
        if (!canEditAssignments) return;

        const targetType = assignmentTypes.find((t) => t.id === targetTypeId);
        const params = new URLSearchParams({
            mode: "stage",
            assignment_type_id: String(targetTypeId),
            assignment_type: targetType?.assignment_type_name || "",
            vehicle_id: item.vehicle_id,
            vehicle_no: item.vehicle_no || "",
            display_no: item.display_no || "",
            service_location_id: item.service_location_id || "",
            branch_id: item.branch_id || "",
        });
        if (item.driver_id) {
            params.set("driver_id", item.driver_id);
        }
        if (item.supplier_id) {
            params.set("supplier_id", item.supplier_id);
        }
        navigate(`/add-vehicle-assignment?${params.toString()}`);
    };

    const closeChainModal = () => {
        setChainModalOpen(false);
        setChainModalRows([]);
        setChainModalError(null);
    };
    const resetAllFilters = () => {
        setSearch("");
        setSearchInput("");
        setFromDate("");
        setToDate("");
        setSelectedType("");
        setSelectedStageId(DEFAULT_STAGE_ID);
        setSelectedModel("");
        setSelectedLocation("");
        setSelectedBranch("");
        setSelectedOutstandingRange("");
        setSelectedStatus("");
        setCurrentPage(1);
    };

    const filteredAssignments = assignments
        .filter((item) => {
            const s = search.toLowerCase();
            const matchesSearch =
                !s ||
                item.full_name?.toLowerCase().includes(s) ||
                item.driver_code?.toLowerCase().includes(s) ||
                item.supplier_code?.toLowerCase().includes(s) ||
                item.vehicle_no?.toLowerCase().includes(s) ||
                item.display_no?.toLowerCase().includes(s) ||
                item.supplier_name?.toLowerCase().includes(s) ||
                item.supplier_type?.toLowerCase().includes(s) ||
                item.mobile?.toLowerCase().includes(s) ||
                item.location_name?.toLowerCase().includes(s) ||
                item.branch_name?.toLowerCase().includes(s) ||
                item.vehicle_model?.toLowerCase().includes(s);

            const matchesDateRange = isWithinDateRange(item.assigned_date, fromDate, toDate);

            const matchesStatus =
                !selectedStatus ||
                (selectedStatus === "active" && item.is_active) ||
                (selectedStatus === "inactive" && !item.is_active);

            return matchesSearch && matchesDateRange && matchesStatus;
        })
        .sort((a, b) => {
            const dateA = a.transaction_date ? new Date(a.transaction_date).getTime() : -Infinity;
            const dateB = b.transaction_date ? new Date(b.transaction_date).getTime() : -Infinity;
            return dateB - dateA;
        });

    const totalPages = Math.max(1, Math.ceil(filteredAssignments.length / ROWS_PER_PAGE));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const paginatedAssignments = filteredAssignments.slice(
        (safeCurrentPage - 1) * ROWS_PER_PAGE,
        safeCurrentPage * ROWS_PER_PAGE
    );
    const totalCount = filteredAssignments.length;
    const activeCount = filteredAssignments.filter((a) => a.is_active).length;
    const inactiveCount = filteredAssignments.filter((a) => !a.is_active).length;
    const outstandingTotal = filteredAssignments.reduce(
        (sum, a) => sum + (Number(a.outstanding_amount) || 0),
        0
    );

    const hasDateFilter = Boolean(fromDate || toDate);
    const hasAdvancedFilter = Boolean(
        selectedType ||
        (selectedStageId && selectedStageId !== DEFAULT_STAGE_ID) ||
        selectedModel ||
        selectedLocation ||
        selectedBranch ||
        selectedOutstandingRange !== "" ||
        selectedStatus
    );
    const hasAnyFilter = Boolean(
        search || searchInput || hasDateFilter || hasAdvancedFilter
    );
    const closeDocModal = () => setDocModalItem(null);
    const openPreview = (url, label) => {
        if (!url) return;
        setPreviewUrl(url);
        setPreviewLabel(label);
    };
    const closePreview = () => {
        setPreviewUrl(null);
        setPreviewLabel("");
    };
    const TOTAL_COLUMNS = 13;

    const actionMenuItem = actionMenuState.id !== null
        ? paginatedAssignments.find((a) => a.id === actionMenuState.id)
        : null;
    const stageMenuItem = stageMenuState.id !== null
        ? paginatedAssignments.find((a) => a.id === stageMenuState.id)
        : null;
    const stageMenuAvailableIds = stageMenuItem ? getAvailableStageIds(stageMenuItem) : [];

    if (!hasAssignmentAccess) {
        return (
            <div className="va-layout">
                <Sidebar />
                <div className="va-content">
                    <Header />
                    <div className="va-page-container">
                        <div className="va-td-center" style={{ padding: 40 }}>
                            You don't have permission to view Vehicle Assignments.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="va-layout">
            <Sidebar />

            <div className="va-content">
                <Header />

                <div className="va-page-container">

                    <div className="va-stat-cards">
                        <div className="va-stat-card va-stat-card--total">
                            <span className="va-stat-label">Total assignments</span>
                            <span className="va-stat-value">{totalCount}</span>
                        </div>
                        <div className="va-stat-card va-stat-card--active">
                            <span className="va-stat-label">Active</span>
                            <span className="va-stat-value va-stat-value--active">{activeCount}</span>
                        </div>
                        <div className="va-stat-card va-stat-card--inactive">
                            <span className="va-stat-label">Inactive</span>
                            <span className="va-stat-value va-stat-value--inactive">{inactiveCount}</span>
                        </div>
                        <div className="va-stat-card va-stat-card--outstanding">
                            <span className="va-stat-label">Outstanding amount</span>
                            <span className="va-stat-value va-stat-value--outstanding">
                                {formatCurrency(outstandingTotal)}
                            </span>
                        </div>
                    </div>

                    <div
                        className="va-toolbar va-toolbar--single-line"
                        style={{
                            display: "flex",
                            flexWrap: "nowrap",
                            alignItems: "center",
                            gap: 8,
                            overflowX: "auto",
                        }}
                    >
                        <div className="va-search-wrap" style={{ flex: "1 1 180px", minWidth: 140 }}>
                            <svg className="va-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                            </svg>
                            <input
                                type="text"
                                className="va-search-box"
                                placeholder="Search Allotments..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") setSearch(searchInput);
                                }}
                                style={{ width: "100%" }}
                            />
                        </div>

                        <select
                            className="va-fy-select"
                            value={selectedFinancialYear}
                            onChange={(e) => setSelectedFinancialYear(e.target.value)}
                            title="Filter by financial year"
                            style={{ flex: "0 0 auto", minWidth: 130 }}
                        >
                            <option value="">Current Year</option>
                            <option value="all">All Financial Years</option>
                            {financialYears.map((fy) => (
                                <option key={fy.id} value={fy.id}>{fy.fy_label}</option>
                            ))}
                        </select>

                        <div className="va-date-field" style={{ flex: "0 0 auto" }}>
                            <input
                                type="date"
                                className="va-date-input"
                                value={fromDate}
                                max={toDate || undefined}
                                onChange={(e) => setFromDate(e.target.value)}
                                title="Filter by assign date — from"
                            />
                        </div>

                        <div className="va-date-field" style={{ flex: "0 0 auto" }}>
                            <input
                                type="date"
                                className="va-date-input"
                                value={toDate}
                                min={fromDate || undefined}
                                onChange={(e) => setToDate(e.target.value)}
                                title="Filter by assign date — to"
                            />
                        </div>

                        <div className="va-filters-dropdown-wrap" style={{ flex: "0 0 auto" }}>
                            <button
                                type="button"
                                ref={filtersBtnRef}
                                className={`va-ghost-btn va-filters-toggle-btn ${filtersOpen ? "va-filters-toggle-btn--active" : ""}`}
                                onClick={toggleFilters}
                                style={{ whiteSpace: "nowrap" }}
                            >
                                <FiFilter size={14} style={{ marginRight: 5 }} />
                                Filters
                                {hasAdvancedFilter && <span className="va-filters-toggle-dot" />}
                            </button>

                            {filtersOpen && (
                                <>
                                    <div
                                        className="va-filters-dropdown-backdrop"
                                        onClick={() => setFiltersOpen(false)}
                                    />
                                    <div
                                        className="va-filters-dropdown-panel"
                                        style={{ top: filtersPos.top, left: filtersPos.left }}
                                    >
                                        <div className="va-filters-dropdown-field">
                                            <label>Status</label>
                                            <select
                                                className="va-fy-select"
                                                value={selectedStatus}
                                                onChange={(e) => setSelectedStatus(e.target.value)}
                                                title="Filter by active/inactive status"
                                                style={{ width: "100%" }}
                                            >
                                                <option value="">All Statuses</option>
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                            </select>
                                        </div>

                                        <div className="va-filters-dropdown-field">
                                            <label>Type</label>
                                            <select
                                                className="va-fy-select"
                                                value={selectedType}
                                                onChange={(e) => setSelectedType(e.target.value)}
                                                title="Filter by type (Own Fleet / DCO / VDP)"
                                                style={{ width: "100%" }}
                                            >
                                                <option value="">All Types</option>
                                                {typeOptions.map((t) => (
                                                    <option key={t} value={t}>{t}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="va-filters-dropdown-field">
                                            <label>Stage</label>
                                            <select
                                                className="va-fy-select"
                                                value={selectedStageId}
                                                onChange={(e) => setSelectedStageId(e.target.value)}
                                                title="Filter by stage"
                                                style={{ width: "100%" }}
                                            >
                                                <option value="">All Stages</option>
                                                {assignmentTypes.map((t) => (
                                                    <option key={t.id} value={t.id}>{t.assignment_type_name}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="va-filters-dropdown-field">
                                            <label>Vehicle Model</label>
                                            <select
                                                className="va-fy-select"
                                                value={selectedModel}
                                                onChange={(e) => setSelectedModel(e.target.value)}
                                                title="Filter by vehicle model"
                                                style={{ width: "100%" }}
                                            >
                                                <option value="">All Models</option>
                                                {modelOptions.map((m) => (
                                                    <option key={m} value={m}>{m}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="va-filters-dropdown-field">
                                            <label>Location</label>
                                            <select
                                                className="va-fy-select"
                                                value={selectedLocation}
                                                onChange={(e) => setSelectedLocation(e.target.value)}
                                                title="Filter by location"
                                                style={{ width: "100%" }}
                                            >
                                                <option value="">All Locations</option>
                                                {locationOptions.map((loc) => (
                                                    <option key={loc.id} value={loc.id}>
                                                        {loc.location_name || loc.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="va-filters-dropdown-field">
                                            <label>Branch</label>
                                            <select
                                                className="va-fy-select"
                                                value={selectedBranch}
                                                onChange={(e) => setSelectedBranch(e.target.value)}
                                                title={
                                                    selectedLocation
                                                        ? "Filter by branch"
                                                        : "Select a location first"
                                                }
                                                disabled={!selectedLocation || branchesLoading}
                                                style={{ width: "100%" }}
                                            >
                                                <option value="">
                                                    {!selectedLocation
                                                        ? "Select a location first"
                                                        : branchesLoading
                                                            ? "Loading branches…"
                                                            : "All Branches"}
                                                </option>
                                                {branchOptions.map((b) => (
                                                    <option key={b.id} value={b.id}>
                                                        {b.branch_name || b.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="va-filters-dropdown-field">
                                            <label>Outstanding Amount</label>
                                            <select
                                                className="va-fy-select"
                                                value={selectedOutstandingRange}
                                                onChange={(e) => setSelectedOutstandingRange(e.target.value)}
                                                title="Filter by outstanding amount"
                                                style={{ width: "100%" }}
                                            >
                                                <option value="">All Amounts</option>
                                                {OUTSTANDING_RANGES.map((range, idx) => (
                                                    <option key={range.label} value={idx}>
                                                        {range.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {hasAdvancedFilter && (
                                            <button
                                                type="button"
                                                className="va-filters-dropdown-clear"
                                                onClick={() => {
                                                    setSelectedType("");
                                                    setSelectedStageId(DEFAULT_STAGE_ID);
                                                    setSelectedModel("");
                                                    setSelectedLocation("");
                                                    setSelectedBranch("");
                                                    setSelectedOutstandingRange("");
                                                    setSelectedStatus("");
                                                }}
                                            >
                                                Clear all filters
                                            </button>
                                        )}

                                        <div className="va-filters-dropdown-footer">
                                            <button
                                                type="button"
                                                className="va-filters-dropdown-done"
                                                onClick={() => setFiltersOpen(false)}
                                            >
                                                Done
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        <button
                            className="va-ghost-btn"
                            onClick={resetAllFilters}
                            disabled={!hasAnyFilter}
                            style={{ flex: "0 0 auto", whiteSpace: "nowrap" }}
                        >
                            Reset
                        </button>

                        {hasAssignmentAccess && (
                            <button
                                className="va-add-btn"
                                onClick={() => navigate("/add-vehicle-assignment")}
                                style={{ flex: "0 0 auto", whiteSpace: "nowrap" }}
                            >
                                + Assign vehicle
                            </button>
                        )}
                    </div>

                    {hasDateFilter && (
                        <div className="va-filter-summary">
                            Showing records assigned
                            {fromDate && <strong>&nbsp;from {new Date(fromDate).toLocaleDateString("en-GB")}</strong>}
                            {toDate && <strong>&nbsp;to {new Date(toDate).toLocaleDateString("en-GB")}</strong>}
                            <button className="va-filter-summary-clear" onClick={() => { setFromDate(""); setToDate(""); }}>
                                Clear dates
                            </button>
                            <span className="va-filter-summary-count">
                                {filteredAssignments.length} of {assignments.length} matching
                            </span>
                        </div>
                    )}

                    <div className="va-table-outer">
                        <div className="va-table-wrapper">
                            <table className="va-data-table">
                                <thead>
                                    <tr>
                                        <th>Transaction Number</th>
                                        <th>Assign Date</th>
                                        <th>Branch name</th>
                                        <th>Supplier Type</th>
                                        <th>Vehicle No</th>
                                        <th>Vehicle Model</th>
                                        <th>Code</th>
                                        <th>Sup/Driver Name</th>
                                        <th>Contact No.</th>
                                        <th>Assign Type</th>
                                        <th>Outstanding</th>
                                        <th className="va-col-stage">Stage</th>
                                        <th className="va-col-manage">Manage</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan={TOTAL_COLUMNS} className="va-td-center">Loading...</td>
                                        </tr>
                                    ) : filteredAssignments.length === 0 ? (
                                        <tr>
                                            <td colSpan={TOTAL_COLUMNS} className="va-td-center">No records found</td>
                                        </tr>
                                    ) : (
                                        paginatedAssignments.map((item) => {
                                            const availableStageIds = getAvailableStageIds(item);
                                            const partyType = getPartyType(item);

                                            return (
                                                <tr
                                                    key={item.id}
                                                    className={canViewAssignments ? "va-row-clickable" : ""}
                                                    onClick={() => {
                                                        if (canViewAssignments) {
                                                            openViewModal(item);
                                                        }
                                                    }}
                                                    title={canViewAssignments ? "Click to view full details" : ""}
                                                >
                                                    <td className="va-mono-cell">
                                                        {item.unique_number || "—"}
                                                        {item.parent_unique_number && (
                                                            <div style={{
                                                                fontSize: 11, color: "#6b7280",
                                                                fontWeight: 400, marginTop: 2,
                                                            }}>
                                                                ↳ from {item.parent_unique_number}
                                                            </div>
                                                        )}
                                                    </td>

                                                  <td>
    {CLOSED_TYPE_IDS.includes(item.assignment_type_id)
        ? (item.relieved_date
            ? new Date(item.relieved_date).toLocaleDateString("en-GB")
            : "—")
        : (item.assigned_date
            ? new Date(item.assigned_date).toLocaleDateString("en-GB")
            : "—")}
</td>

                                                    <td>{item.branch_name || "—"}</td>

                                                    <td>
                                                        {partyType ? (
                                                            <span className="va-badge va-badge--role">
                                                                {partyType}
                                                            </span>
                                                        ) : "—"}
                                                    </td>

                                                    <td>{item.vehicle_no || "—"}</td>
                                                    <td>{item.vehicle_model || "—"}</td>
                                                    <td>{item.driver_code || item.supplier_code || "—"}</td>
                                                    <td>{item.full_name || item.supplier_name || "—"}</td>
                                                    <td>{item.mobile || item.supplier_mobile || "—"}</td>

                                                    <td>
                                                        <span className="va-badge va-badge--role">
                                                            {item.assignment_type || "—"}
                                                        </span>
                                                    </td>

                                                    <td>{formatCurrency(item.outstanding_amount)}</td>

                                                    <td className="va-col-stage">
                                                        {(item.assignment_type_id === 2 || item.assignment_type_id === 3) && !item.is_active ? (
                                                            <span className="va-stage-badge va-stage-badge--closed">
                                                                Closed
                                                            </span>
                                                        ) : (item.assignment_type_id === 2 || item.assignment_type_id === 3) && item.is_active ? (
                                                            <span className="va-stage-badge va-stage-badge--current">
                                                                Current
                                                            </span>
                                                        ) : item.assignment_type_id === 1 && item.is_active && availableStageIds.length > 0 ? (
                                                            canEditAssignments ? (
                                                                <div className="va-stage-menu-wrapper">
                                                                    <button
                                                                        type="button"
                                                                        className={`va-stage-select-btn ${stageMenuState.id === item.id ? "va-stage-select-btn--active" : ""}`}
                                                                        onMouseDown={(e) => e.stopPropagation()}
                                                                        onClick={(e) => toggleStageMenu(e, item.id)}
                                                                    >
                                                                        Select Stage
                                                                        <FiChevronDown size={13} />
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <span className="va-cell-muted">—</span>
                                                            )
                                                        ) : (
                                                            <span className="va-cell-muted">—</span>
                                                        )}
                                                    </td>

                                                    <td className="va-col-manage">
                                                        {hasAssignmentAccess && (
                                                            <div className="va-action-menu-wrapper">
                                                                <button
                                                                    type="button"
                                                                    className={`va-actions-btn ${actionMenuState.id === item.id ? "va-actions-btn--active" : ""}`}
                                                                    onMouseDown={(e) => e.stopPropagation()}
                                                                    onClick={(e) => toggleActionMenu(e, item.id)}
                                                                    aria-label="Row actions"
                                                                    aria-haspopup="true"
                                                                    aria-expanded={actionMenuState.id === item.id}
                                                                >
                                                                    Actions
                                                                    <FiChevronDown size={13} />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {!loading && filteredAssignments.length > 0 && (
                            <div
                                className="va-pagination-bar"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: 8,
                                    padding: "6px 12px",
                                    borderTop: "1px solid #e5e7eb",
                                    background: "#fafafa",
                                }}
                            >
                                <span style={{ fontSize: 12, color: "#6b7280" }}>
                                    {(safeCurrentPage - 1) * ROWS_PER_PAGE + 1}
                                    –{Math.min(safeCurrentPage * ROWS_PER_PAGE, filteredAssignments.length)}{" "}
                                    of {filteredAssignments.length}
                                </span>

                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                    <button
                                        type="button"
                                        className="va-ghost-btn"
                                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                        disabled={safeCurrentPage <= 1}
                                        style={{ padding: "3px 10px", fontSize: 12, lineHeight: 1.2, minHeight: "unset" }}
                                    >
                                        Prev
                                    </button>
                                    <span style={{ fontSize: 12, color: "#374151", padding: "0 4px" }}>
                                        {safeCurrentPage}/{totalPages}
                                    </span>
                                    <button
                                        type="button"
                                        className="va-ghost-btn"
                                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                        disabled={safeCurrentPage >= totalPages}
                                        style={{ padding: "3px 10px", fontSize: 12, lineHeight: 1.2, minHeight: "unset" }}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                </div>
            </div>

            {actionMenuItem &&
                createPortal(
                    <>
                        <div
                            className="va-menu-backdrop"
                            onClick={closeActionMenu}
                        />

                        <div
                            className="va-action-menu-dropdown"
                            style={{
                                position: "fixed",
                                top: actionMenuState.top,
                                left: actionMenuState.left,
                                zIndex: 2147483647,
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {canViewAssignments && (
                                <button
                                    type="button"
                                    className="va-action-menu-item"
                                    onClick={() => {
                                        const item = actionMenuItem;
                                        closeActionMenu();
                                        if (item) openViewModal(item);
                                    }}
                                >
                                    View
                                </button>
                            )}

                            {canEditAssignments && (
                                <button
                                    type="button"
                                    className="va-action-menu-item"
                                    onClick={() => {
                                        const item = actionMenuItem;
                                        closeActionMenu();
                                        if (item) {
                                            navigate(`/add-vehicle-assignment/${item.id}`);
                                        }
                                    }}
                                >
                                    Edit
                                </button>
                            )}

                            {canDeleteAssignments && (
                                <button
                                    type="button"
                                    className="va-action-menu-item va-action-menu-item--delete"
                                    onClick={() => {
                                        const item = actionMenuItem;
                                        closeActionMenu();
                                        if (item) handleDelete(item.id);
                                    }}
                                >
                                    Delete
                                </button>
                            )}
                        </div>
                    </>,
                    document.body
                )
            }

            {stageMenuItem &&
                createPortal(
                    <>
                        <div
                            className="va-menu-backdrop"
                            onClick={closeStageMenu}
                        />

                        <div
                            className="va-stage-menu-dropdown"
                            style={{
                                position: "fixed",
                                top: stageMenuState.top,
                                left: stageMenuState.left,
                                zIndex: 2147483647,
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                        >
                            {stageMenuAvailableIds.map((stageId) => {
                                const stageObj = assignmentTypes.find(
                                    (t) => String(t.id) === String(stageId)
                                );
                                const stageName = stageObj?.assignment_type_name || "";

                                return (
                                    <button
                                        key={stageId}
                                        type="button"
                                        className={`va-stage-menu-item va-stage-menu-item--${stageName
                                            .toLowerCase()
                                            .replace(/\s+/g, "-")}`}
                                        onClick={() => {
                                            const item = stageMenuItem;
                                            closeStageMenu();
                                            if (item) openStageForm(item, stageId);
                                        }}
                                    >
                                        {stageName}
                                    </button>
                                );
                            })}
                        </div>
                    </>,
                    document.body
                )
            }

            {docModalItem && (
                <div className="va-docmodal-overlay" onClick={closeDocModal}>
                    <div className="va-docmodal" onClick={(e) => e.stopPropagation()}>
                        <div className="va-docmodal-header">
                            <div>
                                <h3>Documents</h3>
                                <p>
                                    {"— " + (docModalItem.full_name || "Unassigned")}
                                    {docModalItem.vehicle_no ? " " + docModalItem.vehicle_no : ""}
                                </p>
                            </div>
                            <button className="va-modal-close" onClick={closeDocModal} aria-label="Close">
                                <FiX size={18} />
                            </button>
                        </div>

                        <div className="va-docmodal-grid">
                            {DOC_FIELDS.map(({ key, label }) => {
                                const url = docModalItem[key];
                                const cardLabel = (docModalItem.full_name || "Assignment") + " — " + label;
                                return (
                                    <div className="va-doc-card" key={key}>
                                        <span className="va-doc-card-label">{label}</span>
                                        {url ? (
                                            isImageUrl(url) ? (
                                                <img
                                                    src={url}
                                                    alt={label}
                                                    className="va-doc-card-img"
                                                    onClick={() => openPreview(url, cardLabel)}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = "";
                                                        e.target.classList.add("va-doc-card-img--broken");
                                                    }}
                                                />
                                            ) : (
                                                <div className="va-doc-card-file" onClick={() => openPreview(url, cardLabel)}>
                                                    <FiFileText size={28} />
                                                </div>
                                            )
                                        ) : (
                                            <div className="va-doc-card-empty">
                                                <span>Not uploaded</span>
                                            </div>
                                        )}
                                        {url && (
                                            <a href={url} target="_blank" rel="noreferrer" className="va-doc-card-view">
                                                View full
                                            </a>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="va-docmodal-footer">
                            {docCount(docModalItem)} of {DOC_FIELDS.length} documents uploaded
                        </div>
                    </div>
                </div>
            )}

            {chainModalOpen && (
                <div className="va-docmodal-overlay" onClick={closeChainModal}>
                    <div className="va-docmodal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
                        <div className="va-docmodal-header">
                            <div>
                                <h3>Vehicle Lifecycle</h3>
                                <p>{"— " + chainModalLabel}</p>
                            </div>
                            <button className="va-modal-close" onClick={closeChainModal} aria-label="Close">
                                <FiX size={18} />
                            </button>
                        </div>

                        {chainModalLoading && (
                            <div style={{ padding: "24px 0", textAlign: "center", color: "#6b7280" }}>
                                Loading chain history…
                            </div>
                        )}

                        {!chainModalLoading && chainModalError && (
                            <div style={{ padding: "16px 0", textAlign: "center", color: "#b91c1c" }}>
                                {chainModalError}
                            </div>
                        )}

                        {!chainModalLoading && !chainModalError && chainModalRows.length > 0 && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "12px 0" }}>
                                {chainModalRows.map((row, idx) => {
                                    const badge = STAGE_BADGE_COLOR[row.assignment_type] || STAGE_BADGE_COLOR.Dispatch;
                                    return (
                                        <div
                                            key={row.id}
                                            style={{
                                                display: "flex", alignItems: "flex-start", gap: 12,
                                                padding: "10px 14px", borderRadius: 8,
                                                border: "1px solid #e5e7eb",
                                                background: row.is_active ? "#fafffe" : "#fff",
                                            }}
                                        >
                                            <div style={{
                                                width: 24, height: 24, borderRadius: "50%",
                                                background: badge.bg, border: `2px solid ${badge.border}`,
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                fontSize: 11, fontWeight: 700, color: badge.color,
                                                flexShrink: 0, marginTop: 2,
                                            }}>
                                                {idx + 1}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                                    <span style={{
                                                        fontSize: 12, fontWeight: 700, color: badge.color,
                                                        background: badge.bg, border: `1px solid ${badge.border}`,
                                                        borderRadius: 999, padding: "2px 10px",
                                                    }}>
                                                        {row.assignment_type}
                                                    </span>
                                                    <span className="va-mono-cell" style={{ fontSize: 12 }}>
                                                        {row.unique_number}
                                                    </span>
                                                    {row.is_active && (
                                                        <span style={{ fontSize: 11, fontWeight: 700, color: "#15803d" }}>
                                                            ● Active
                                                        </span>
                                                    )}
                                                </div>
                                                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                                                    {row.driver_name || row.supplier_name || "Unassigned"}
                                                    {" · "}
                                                    {row.assigned_date
                                                        ? new Date(row.assigned_date).toLocaleDateString("en-GB")
                                                        : "—"}
                                                    {row.relieved_date && (
                                                        <> {" → "}{new Date(row.relieved_date).toLocaleDateString("en-GB")}</>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {previewUrl && (
                <div className="va-preview-overlay" onClick={closePreview}>
                    <div className="va-preview-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="va-preview-header">
                            <span>{previewLabel}</span>
                            <button className="va-modal-close" onClick={closePreview} aria-label="Close">
                                <FiX size={16} />
                            </button>
                        </div>
                        <div className="va-preview-body">
                            {isImageUrl(previewUrl) ? (
                                <img src={previewUrl} alt={previewLabel} className="va-preview-image" />
                            ) : (
                                <iframe src={previewUrl} title={previewLabel} className="va-preview-pdf" />
                            )}
                        </div>
                        <div className="va-preview-footer">
                            <a href={previewUrl} target="_blank" rel="noreferrer" className="va-preview-link">
                                Open in new tab
                            </a>
                            <a href={previewUrl} download className="va-preview-link">
                                Download
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {viewModalItem && (
                <div className="va-docmodal-overlay va-viewmodal-overlay" onClick={closeViewModal}>
                    <div className="va-docmodal va-viewmodal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 780 }}>
                        <div className="va-docmodal-header">
                            <div>
                                <h3>{viewModalItem.full_name || viewModalItem.supplier_name || "Assignment"} details</h3>
                                <p>
                                    {"— " + (viewModalItem.unique_number || "")}
                                    {viewModalItem.vehicle_no ? " · " + viewModalItem.vehicle_no : ""}
                                    {viewModalLoading && <span style={{ marginLeft: 8, color: "#6b7280" }}>Refreshing…</span>}
                                </p>
                            </div>
                            <div className="va-viewmodal-header-actions va-no-print">
                                <button type="button" className="va-ghost-btn" onClick={handlePrintView}>
                                    Print
                                </button>
                                <button
                                    type="button"
                                    className="va-ghost-btn"
                                    onClick={handleDownloadView}
                                    disabled={pdfGenerating || viewModalLoading}
                                >
                                    {pdfGenerating ? "Preparing…" : "Download"}
                                </button>
                                <button className="va-modal-close" onClick={closeViewModal} aria-label="Close">
                                    <FiX size={18} />
                                </button>
                            </div>
                        </div>

                        {viewModalError && (
                            <div style={{ padding: "8px 20px", color: "#b91c1c", fontSize: 13 }}>
                                {viewModalError}
                            </div>
                        )}

                        <div className="va-viewmodal-body" style={{ opacity: viewModalLoading ? 0.6 : 1 }}>
<section className="va-view-section va-view-section-media">
                                <h4 className="va-viewmodal-subhead">Photos</h4>

                                <div className="va-viewmodal-media-grid va-viewmodal-primary-photos">
                                    <div className="va-media-card va-media-card-vehicle">
                                        <div className="va-media-card-header">
                                            <span className="va-media-card-icon">🚘</span>
                                            <div>
                                                <strong>Vehicle Photo</strong>
                                                {/* <small>Vehicle image</small> */}
                                            </div>
                                        </div>

                                        {(() => {
                                            const url = getVehiclePhoto(viewModalItem);
                                            return url ? (
                                                <img
                                                    src={url}
                                                    alt="Vehicle"
                                                    className="va-media-preview"
                                                    onClick={() => openPreview(url, "Vehicle Photo")}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = "";
                                                        e.target.classList.add("va-doc-card-img--broken");
                                                    }}
                                                />
                                            ) : (
                                                <div className="va-media-empty">
                                                    Not uploaded
                                                </div>
                                            );
                                        })()}
                                    </div>

                                    <div className="va-media-card va-media-card-person">
                                        <div className="va-media-card-header">
                                            <span className="va-media-card-icon">👤</span>
                                            <div>
                                                <strong>{viewModalItem.driver_id ? "Driver Photo" : "Supplier Photo"}</strong>
                                                {/* <small>{viewModalItem.driver_id ? "Driver image" : "Supplier image"}</small> */}
                                            </div>
                                        </div>

                                        {(() => {
                                            const { url, name } = getAssignedPhoto(viewModalItem);
                                            return url ? (
                                                <img
                                                    src={url}
                                                    alt={name || "Photo"}
                                                    className="va-media-preview"
                                                    onClick={() => openPreview(url, name || "Photo")}
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = "";
                                                        e.target.classList.add("va-doc-card-img--broken");
                                                    }}
                                                />
                                            ) : (
                                                <div className="va-media-empty">
                                                    Not uploaded
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            </section>

                            <section className="va-view-section va-view-section-overview">
                                <h4 className="va-viewmodal-subhead">Overview</h4>
                                <div className="va-viewmodal-grid">
                                    <div className="va-viewmodal-field">
                                        <label>Transaction Number</label>
                                        <span>{viewModalItem.unique_number || "—"}</span>
                                    </div>
                                    <div className="va-viewmodal-field">
                                        <label>Transaction ID</label>
                                        <span>{viewModalItem.transaction_id || "—"}</span>
                                    </div>
                                    <div className="va-viewmodal-field">
                                        <label>Chain ID</label>
                                        <span>{viewModalItem.chain_id || "—"}</span>
                                    </div>
                                    <div className="va-viewmodal-field">
                                        <label>Assignment Type</label>
                                        <span>{viewModalItem.assignment_type || "—"}</span>
                                    </div>
                                    <div className="va-viewmodal-field">
                                        <label>Financial Year</label>
                                        <span>{viewModalItem.financial_year_label || "—"}</span>
                                    </div>
                                    <div className="va-viewmodal-field">
                                        <label>Remarks</label>
                                        <span>{viewModalItem.remarks || "—"}</span>
                                    </div>
                                </div>
                            </section>

<section className="va-view-section va-view-section-driver">
                                <h4 className="va-viewmodal-subhead">Driver</h4>
                                <div className="va-viewmodal-grid">
                                    <div className="va-viewmodal-field">
                                        <label>Driver Name</label>
                                        <span>{viewModalItem.full_name || "—"}</span>
                                    </div>
                                    <div className="va-viewmodal-field">
                                        <label>Driver Code</label>
                                        <span>{viewModalItem.driver_code || "—"}</span>
                                    </div>
                                    <div className="va-viewmodal-field">
                                        <label>Driver Mobile</label>
                                        <span>{viewModalItem.mobile || "—"}</span>
                                    </div>
                                    <div className="va-viewmodal-field">
                                        <label>Driver Email</label>
                                        <span>{viewModalItem.email || "—"}</span>
                                    </div>
                                </div>
                            </section>

<section className="va-view-section va-view-section-supplier">
                                <h4 className="va-viewmodal-subhead">Supplier</h4>
                                <div className="va-viewmodal-grid">
                                    <div className="va-viewmodal-field">
                                        <label>Supplier Name</label>
                                        <span>{viewModalItem.supplier_name || "—"}</span>
                                    </div>
                                    <div className="va-viewmodal-field">
                                        <label>Supplier Code</label>
                                        <span>{viewModalItem.supplier_code || "—"}</span>
                                    </div>
                                    <div className="va-viewmodal-field">
                                        <label>Supplier Mobile</label>
                                        <span>{viewModalItem.supplier_mobile || "—"}</span>
                                    </div>
                                    <div className="va-viewmodal-field">
                                        <label>Supplier Type</label>
                                        <span>{viewModalItem.supplier_type || getPartyType(viewModalItem) || "—"}</span>
                                    </div>
                                    <div className="va-viewmodal-field">
                                        <label>Outstanding Amount</label>
                                        <span>{formatCurrency(viewModalItem.outstanding_amount)}</span>
                                    </div>
                                </div>
                            </section>

<section className="va-view-section va-view-section-vehicle">
                                <h4 className="va-viewmodal-subhead">Vehicle</h4>
                                <div className="va-viewmodal-grid">
                                    <div className="va-viewmodal-field">
                                        <label>Vehicle Number</label>
                                        <span>{viewModalItem.vehicle_no || "—"}</span>
                                    </div>
                                    <div className="va-viewmodal-field">
                                        <label>Display Number</label>
                                        <span>{viewModalItem.display_no || "—"}</span>
                                    </div>
                                    <div className="va-viewmodal-field">
                                        <label>Vehicle Model</label>
                                        <span>{viewModalItem.vehicle_model || "—"}</span>
                                    </div>
                                    <div className="va-viewmodal-field">
                                        <label>Odometer (KM)</label>
                                        <span>{viewModalItem.vehicle_odometer_km ?? "—"}</span>
                                    </div>
                                </div>
                            </section>

<section className="va-view-section va-view-section-location">
                                <h4 className="va-viewmodal-subhead">Location</h4>
                                <div className="va-viewmodal-grid">
                                    <div className="va-viewmodal-field">
                                        <label>Service Location</label>
                                        <span>{viewModalItem.location_name || "—"}</span>
                                    </div>
                                    <div className="va-viewmodal-field">
                                        <label>Branch Name</label>
                                        <span>{viewModalItem.branch_name || "—"}</span>
                                    </div>
                                </div>
                            </section>

<section className="va-view-section va-view-section-dates">
                                <h4 className="va-viewmodal-subhead">Dates</h4>
                                <div className="va-viewmodal-grid">
                                    <div className="va-viewmodal-field">
                                        <label>Assigned Date</label>
                                        <span>
                                            {viewModalItem.assigned_date
                                                ? new Date(viewModalItem.assigned_date).toLocaleDateString("en-GB")
                                                : "—"}
                                        </span>
                                    </div>
                                    <div className="va-viewmodal-field">
                                        <label>Closure Date</label>
                                        <span>
                                            {viewModalItem.relieved_date
                                                ? new Date(viewModalItem.relieved_date).toLocaleDateString("en-GB")
                                                : "—"}
                                        </span>
                                    </div>
                                    <div className="va-viewmodal-field">
                                        <label>Transaction Date</label>
                                        <span>
                                            {viewModalItem.transaction_date
                                                ? new Date(viewModalItem.transaction_date).toLocaleString("en-GB")
                                                : "—"}
                                        </span>
                                    </div>
                                    <div className="va-viewmodal-field">
                                        <label>Created On</label>
                                        <span>
                                            {viewModalItem.created_at
                                                ? new Date(viewModalItem.created_at).toLocaleString("en-GB")
                                                : "—"}
                                        </span>
                                    </div>
                                </div>
                            </section>

<section className="va-view-section va-view-section-audit">
                                <h4 className="va-viewmodal-subhead">Audit</h4>
                                <div className="va-viewmodal-grid">
                                    <div className="va-viewmodal-field">
                                        <label>Created By</label>
                                        <span>{viewModalItem.created_by_name || "—"}</span>
                                    </div>
                                    <div className="va-viewmodal-field">
                                        <label>Updated By</label>
                                        <span>{viewModalItem.updated_by_name || "—"}</span>
                                    </div>
                                </div>
                            </section>

                            <section className="va-view-section va-view-section-media va-view-section-secondary-documents">
                                <h4 className="va-viewmodal-subhead">Photos & Documents</h4>
                                <div className="va-viewmodal-media-grid va-viewmodal-secondary-documents">
                                    {DOC_FIELDS.map(({ key, label }) => {
                                        const url = resolveMediaUrl(viewModalItem[key]);
                                        return (
                                            <div className="va-media-card va-media-card-document" key={key}>
                                                <div className="va-media-card-header">
                                                    <span className="va-media-card-icon">📄</span>
                                                    <div>
                                                        <strong>{label}</strong>
                                                        <small>Uploaded document</small>
                                                    </div>
                                                </div>

                                                {url ? (
                                                    isImageUrl(url) ? (
                                                        <img
                                                            src={url}
                                                            alt={label}
                                                            className="va-media-preview"
                                                            onClick={() => openPreview(url, label)}
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = "";
                                                                e.target.classList.add("va-doc-card-img--broken");
                                                            }}
                                                        />
                                                    ) : (
                                                        <div
                                                            className="va-media-file"
                                                            onClick={() => openPreview(url, label)}
                                                        >
                                                            <FiFileText size={34} />
                                                            <span>Open Document</span>
                                                        </div>
                                                    )
                                                ) : (
                                                    <div className="va-media-empty">
                                                        Not uploaded
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}